import type { HandleChangeAnswerType } from '~/components/Interaction/Form/Question/model';
import type { AnswerChoicesRankingType } from '~/model/AnswerUserType';
import type { ChoiceType } from '~/model/ChoiceType';
import type { UriType } from '~/model/GlobalTypes';
import type { QuestionChoiceType } from '~/model/QuestionChoiceType';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { CheckboxGroup } from '~/components/commons/CheckboxGroup/CheckboxGroup';
import { MainColumn } from '~/components/commons/MainColumn/MainColumn';
import {
  getPicturesList,
  shuffleArray,
} from '~/components/Interaction/Form/Question/utils';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { useFocus } from '~/hooks/useFocus';
import { ChoicePicture } from './ChoicePicture';
import { PicturesGallery, usePicturesGallery } from './PicturesGallery';

export const getRankingValues = (uris: UriType[]) => {
  return {
    choices: uris || [],
    ranking: uris?.map(uri => getIdFromUri(uri)) || [],
  };
};

export const RankingPictures = ({
  answerUser,
  answerChoices: questionChoices,
  questionId,
  handleChangeAnswer,
  type,
  langValue,
  disabled = false,
  choicesRandomOrder,
}: RankingPicturesProps) => {
  const t = useTranslations('FORM');

  const [inputRef, setInputFocus] = useFocus();

  const [hasFocused, setHasFocused] = useState(false);

  const choices = useMemo(
    () => choicesRandomOrder ? shuffleArray(questionChoices) : questionChoices,
    [questionId, choicesRandomOrder],
  );

  const answer = answerUser[questionId];
  const answerChoices = answer?.answer_choices as AnswerChoicesRankingType;
  const userChoices = answerChoices?.choices;
  const userRanking = answerChoices?.ranking;

  const checkboxValues
    = userRanking?.map(id =>
      userChoices?.find(uri => getIdFromUri(uri) === +id),
    ) || null;

  const nbChoices = choices.length;

  const maxChoices = nbChoices > 2 ? 3 : nbChoices;

  const typeClass = 'control-checkboxes';

  const countClass = `${typeClass}--${nbChoices}`;

  const picturesList = getPicturesList(choices);

  const picturesListGallery = picturesList?.map(uri =>
    getResizedPictureUrl(uri, '1600x0'),
  );

  const shouldUseCenteredLayout = nbChoices < 3;

  const picturesLayout = shouldUseCenteredLayout
    ? journeyColumnTemplate
    : undefined;

  const { isOpen, setIsOpen, photoIndex, setPhotoIndex, openGallery }
    = usePicturesGallery(picturesListGallery);

  const handleChangeChoice = (values: UriType[]) => {
    const value = getRankingValues(values);
    handleChangeAnswer(value, questionId, type);
  };

  useEffect(() => setHasFocused(false), [questionId]);

  useLayoutEffect(() => {
    if (!hasFocused && !disabled && !!inputRef.current) {
      setInputFocus();
      setHasFocused(true);
    }
  }, [!!inputRef.current, disabled]);

  return (
    <>
      <PicturesGallery
        key={`gallery-${questionId}`}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        photoIndex={photoIndex}
        setPhotoIndex={setPhotoIndex}
        picturesList={picturesListGallery}
        picturesListFallback={picturesList}
      />
      <MainColumn column={journeyColumnTemplate}>
        <div className="ranking-picture__info vp-body-m">
          {t('RANKING_PICTURE_INFO', { count: maxChoices })}
        </div>
      </MainColumn>
      <MainColumn column={picturesLayout}>
        <div
          data-cy="choices"
          data-testid="choices"
          className={classNames('control-inline', [typeClass], [countClass])}
        >
          <CheckboxGroup
            name="ranking-picture-checkbox-group"
            key={questionId}
            value={checkboxValues || []}
            onChange={handleChangeChoice}
          >
            {Checkbox => (
              <>
                {choices.map((choice: QuestionChoiceType, index: number) => {
                  const choiceUri = choice['@id'];
                  const choiceId = choice.id;
                  const isChecked = checkboxValues?.includes(
                    choiceUri,
                  );
                  const choiceIndex = checkboxValues?.findIndex(
                    uri => uri === choiceUri,
                  );
                  const shouldBeDisabled
                    = userChoices?.length === maxChoices
                      && !userChoices.find(uri => uri === choiceUri);
                  const ranking = choiceIndex + 1;
                  const shouldHaveRef = index === 0;
                  const label = choice.title;

                  return (
                    <div
                      className="form-group checkbox"
                      data-testid="checkbox"
                      key={`${questionId}-${choiceId}`}
                    >
                      <div
                        className={classNames(
                          'control-checkbox',
                          'control-ranking-pictures',
                          {
                            'control-checkbox--selected': isChecked,
                          },
                        )}
                      >
                        <ChoicePicture
                          choice={choice}
                          openGallery={openGallery}
                          isChecked={false}
                        />
                        <span className="control-outline" />
                        {isChecked && (
                          <span className="control-checked-ranking">
                            {ranking}
                          </span>
                        )}
                        <span
                          className={classNames('control-legend', {
                            'control-legend--selected': isChecked,
                          })}
                        >
                          <Checkbox
                            className={classNames('control-input')}
                            value={choiceUri}
                            id={`choice-${choiceId}`}
                            data-cy="check"
                            disabled={disabled || shouldBeDisabled}
                          />
                          <label
                            ref={shouldHaveRef ? inputRef : null}
                            className="form__label"
                            data-cy="choiceRankingPicture"
                            htmlFor={`choice-${choiceId}`}
                            title={choice.title}
                          >
                            <span lang={langValue}>{label}</span>
                          </label>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </CheckboxGroup>
        </div>
      </MainColumn>
    </>
  );
};

export interface RankingPicturesProps {
  answerUser: unknown;
  answerChoices: ChoiceType[];
  questionId: string;
  type: string;
  handleChangeAnswer: HandleChangeAnswerType;
  langValue: string;
  disabled?: boolean;
  choicesRandomOrder: boolean;
}
